import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { MaybeRefOrGetter } from "vue";
import { computed, toValue } from "vue";
import {
	fetchPlatformAccess,
	type PlatformAccess,
	platformAccessKey,
} from "@/shared/agent-display/policy";
import { request } from "@/shared/http/client";
import type {
	AuditEntry,
	LogEntry,
	PlatformNotice,
	PresencePerson,
	PurgeOutcome,
	SupportSession,
	TenantDossier,
	TenantPage,
	TenantStatus,
	UsageFilters,
	UsageHistoryPage,
	UsageReport,
	UsageRow,
	UsageTurnDetail,
} from "./platform.types";

// The shapes live next door, in `platform.types.ts`, and are re-exported here so
// every screen keeps importing them from one place. Splitting them out is what
// keeps this file under the size rule now that it answers eleven questions.
export type {
	AuditEntry,
	BuiltThing,
	DossierActivity,
	DossierPerson,
	LogEntry,
	PresencePerson,
	PurgeOutcome,
	SupportSession,
	TenantDossier,
	TenantOverview,
	TenantPage,
	TenantStatus,
	UsageCredits,
	UsageFilters,
	UsageHistoryPage,
	UsageReport,
	UsageRow,
	UsageSummary,
	UsageToolRow,
	UsageTurnDetail,
	UsageTurnRow,
	UsageTurnStep,
} from "./platform.types";

/**
 * Whether this session administers the installation.
 *
 * A CONSULTA mora em `shared/agent-display/policy.ts` desde 24/08: a mesma rota
 * responde também O QUE CADA MODO MOSTRA, e o painel do agente — que é outra
 * feature e não pode importar esta — tinha de a ler. Duas consultas ao mesmo
 * `/v3/platform/access` seriam duas chaves de cache para o mesmo facto.
 *
 * O que fica aqui é a VISTA que esta feature quer: um booleano. Uma chamada
 * simples e não só um hook, porque o guard do router corre fora de qualquer
 * componente e precisa da resposta antes de um existir.
 */
export async function readPlatformAccess(): Promise<boolean> {
	return (await fetchPlatformAccess()).platformAdmin;
}

/**
 * A mesma resposta, em cache, para um componente a decidir se desenha.
 *
 * `false` enquanto carrega — um item de menu ausente que aparece um instante
 * depois é uma mentira menor do que um que aparece e é depois recusado.
 */
export function usePlatformAccess(enabled: () => boolean = () => true) {
	return useQuery({
		enabled,
		queryKey: platformAccessKey,
		queryFn: fetchPlatformAccess,
		staleTime: 60_000,
		select: (access: PlatformAccess) => access.platformAdmin,
	});
}

/**
 * The list, narrowed and paged.
 *
 * The filters are IN the key. They have to be: with a fixed key every page and
 * every search would read the first page out of the cache and the screen would
 * quietly stop responding to its own controls.
 */
export function usePlatformTenants(
	enabled: MaybeRefOrGetter<boolean>,
	q: MaybeRefOrGetter<string>,
	status: MaybeRefOrGetter<string>,
	page: MaybeRefOrGetter<number>,
	pageSize: MaybeRefOrGetter<number>,
	kind: MaybeRefOrGetter<string> = "",
	sort: MaybeRefOrGetter<string> = "",
	dir: MaybeRefOrGetter<string> = "",
) {
	return useQuery({
		queryKey: [
			"platform",
			"tenants",
			() => toValue(q),
			() => toValue(status),
			() => toValue(page),
			() => toValue(pageSize),
			() => toValue(kind),
			() => toValue(sort),
			() => toValue(dir),
		] as const,
		queryFn: async (): Promise<TenantPage> => {
			const query = new URLSearchParams({
				page: String(toValue(page)),
				pageSize: String(toValue(pageSize)),
			});
			if (toValue(q)) query.set("q", String(toValue(q)));
			if (toValue(status)) query.set("status", String(toValue(status)));
			if (toValue(kind)) query.set("kind", String(toValue(kind)));
			if (toValue(sort)) query.set("sort", String(toValue(sort)));
			if (toValue(dir)) query.set("dir", String(toValue(dir)));
			return request<TenantPage>(`/v3/platform/tenants?${query}`);
		},
		enabled: () => toValue(enabled),
		// The previous page stays on screen while the next one loads. Without it the
		// table blanks on every click, which reads as an error rather than as paging.
		placeholderData: (previous) => previous,
	});
}

export function useTenantDossier(id: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "tenant", () => toValue(id)] as const,
		queryFn: () => request<TenantDossier>(`/v3/platform/tenants/${toValue(id)}`),
		enabled: () => toValue(enabled) && Boolean(toValue(id)),
	});
}

/**
 * Spend, per workspace / day / model.
 *
 * `tenantId` narrows it to one workspace — the dossier's fourth tab, and the one
 * read that waits until that tab is opened. There is deliberately no link from
 * here into a tenant's own `/usage`: this screen asks how much, never what.
 *
 * EMENDA 14/08/2026 — decisão do dono: "tem que mostrar de todos com os
 * filtros". A frase acima descreve ESTE hook agregado, mas deixou de ser a
 * regra da superfície: o conteúdo das chamadas de todos os workspaces viaja em
 * `usePlatformUsageHistory`/`usePlatformUsageTurn` abaixo, como na V2.
 */
export function usePlatformUsage(
	enabled: MaybeRefOrGetter<boolean>,
	days: MaybeRefOrGetter<number>,
	tenantId?: MaybeRefOrGetter<string | null>,
) {
	return useQuery({
		queryKey: [
			"platform",
			"usage",
			() => toValue(days),
			() => (tenantId ? toValue(tenantId) : null),
		] as const,
		queryFn: async (): Promise<UsageRow[]> => {
			const query = new URLSearchParams({ days: String(toValue(days)) });
			const one = tenantId ? toValue(tenantId) : null;
			if (one) query.set("tenantId", one);
			const res = await request<{ rows: UsageRow[] }>(`/v3/platform/usage?${query}`);
			return res.rows;
		},
		enabled: () => toValue(enabled),
	});
}

/**
 * O painel de uso INTEIRO: KPIs, linhas, créditos por workspace e — quando um
 * workspace foi escolhido — as ferramentas dele.
 *
 * Um hook próprio em vez de crescer `usePlatformUsage`: aquele devolve só as
 * LINHAS e o `TenantCostPanel` do dossiê lê exatamente isso; mudar a forma da
 * resposta dele seria re-desenhar uma tela que não pediu nada. Os filtros vão
 * inteiros na CHAVE, pela mesma razão de sempre: com chave fixa, todo recorte
 * leria o primeiro da cache e a tela pararia de obedecer aos próprios controles.
 */
/**
 * Os recortes do painel, como a querystring os diz — UMA tradução para o
 * relatório e para o histórico, porque os dois falam os mesmos filtros e duas
 * cópias é como um deles deixa de falar um.
 *
 * Dias fechados nas duas pontas: o servidor compara instantes, e um `to` à
 * meia-noite cortaria fora o dia que a pessoa acabou de escolher.
 */
function usageSearchParams(f: UsageFilters): URLSearchParams {
	const query = new URLSearchParams();
	if (f.from) query.set("from", new Date(`${f.from}T00:00:00`).toISOString());
	if (f.to) query.set("to", new Date(`${f.to}T23:59:59.999`).toISOString());
	if (f.tenantId) query.set("tenantId", f.tenantId);
	if (f.agent) query.set("agent", f.agent);
	if (f.source) query.set("source", f.source);
	if (f.model) query.set("model", f.model);
	if (f.provider) query.set("provider", f.provider);
	if (f.onlyErrors) query.set("onlyErrors", "true");
	if (f.kind) query.set("kind", f.kind);
	return query;
}

export function usePlatformUsageReport(
	enabled: MaybeRefOrGetter<boolean>,
	filters: MaybeRefOrGetter<UsageFilters>,
) {
	return useQuery({
		queryKey: computed(() => ["platform", "usage-report", toValue(filters)] as const),
		queryFn: async (): Promise<UsageReport> => {
			const query = usageSearchParams(toValue(filters));
			return request<UsageReport>(`/v3/platform/usage?${query}`);
		},
		enabled: () => toValue(enabled),
		// A resposta anterior fica na tela enquanto a nova chega — um painel que
		// pisca em branco a cada filtro lê como erro, não como recorte.
		placeholderData: (previous) => previous,
	});
}

/** Quantas linhas por página do histórico. Uma constante, para a paginação bater. */
export const USAGE_HISTORY_PAGE_SIZE = 25;

/**
 * O histórico chamada-a-chamada, cross-tenant — a emenda de 14/08/2026.
 *
 * Os MESMOS filtros do painel, mais a busca textual, o POLEGAR e a página. Tudo na CHAVE,
 * pela razão de sempre: com chave fixa, todo recorte leria a primeira página da
 * cache e a tela pararia de obedecer aos próprios controles. As datas fecham
 * nas duas pontas exatamente como no relatório — um `to` à meia-noite cortaria
 * o dia que a pessoa acabou de escolher.
 */
export function usePlatformUsageHistory(
	enabled: MaybeRefOrGetter<boolean>,
	filters: MaybeRefOrGetter<UsageFilters>,
	q: MaybeRefOrGetter<string>,
	page: MaybeRefOrGetter<number>,
	rating: MaybeRefOrGetter<-1 | 1 | null>,
) {
	return useQuery({
		queryKey: computed(
			() =>
				[
					"platform",
					"usage-history",
					toValue(filters),
					toValue(q),
					toValue(rating),
					toValue(page),
				] as const,
		),
		queryFn: async (): Promise<UsageHistoryPage> => {
			const query = usageSearchParams(toValue(filters));
			const text = toValue(q).trim();
			if (text) query.set("q", text);
			const thumb = toValue(rating);
			if (thumb) query.set("rating", String(thumb));
			query.set("page", String(toValue(page)));
			query.set("pageSize", String(USAGE_HISTORY_PAGE_SIZE));
			return request<UsageHistoryPage>(`/v3/platform/usage/history?${query}`);
		},
		enabled: () => toValue(enabled),
		// A página anterior fica na tela enquanto a nova chega — piscar em branco a
		// cada clique lê como erro, não como paginação.
		placeholderData: (previous) => previous,
	});
}

/**
 * Uma chamada, inteira — só buscada quando alguém ABRE a linha: o prompt
 * completo é o texto mais pesado do sistema, e a lista já trouxe o preview.
 */
export function usePlatformUsageTurn(
	tenantId: MaybeRefOrGetter<string>,
	turnId: MaybeRefOrGetter<string>,
) {
	return useQuery({
		queryKey: computed(
			() => ["platform", "usage-turn", toValue(tenantId), toValue(turnId)] as const,
		),
		queryFn: async (): Promise<UsageTurnDetail> => {
			const res = await request<{ turn: UsageTurnDetail }>(
				`/v3/platform/usage/history/${toValue(tenantId)}/${toValue(turnId)}`,
			);
			return res.turn;
		},
		enabled: computed(() => toValue(tenantId).length > 0 && toValue(turnId).length > 0),
	});
}

export function usePlatformAudit(enabled: MaybeRefOrGetter<boolean>, limit = 50) {
	return useQuery({
		queryKey: ["platform", "audit"] as const,
		queryFn: async (): Promise<AuditEntry[]> => {
			const res = await request<{ entries: AuditEntry[] }>(`/v3/platform/audit?limit=${limit}`);
			return res.entries;
		},
		enabled: () => toValue(enabled),
	});
}

export function usePlatformPresence(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "presence"] as const,
		queryFn: async () => {
			const res = await request<{
				tenants: Array<{
					tenantId: string;
					slug: string;
					name: string;
					people: PresencePerson[];
				}>;
			}>("/v3/platform/presence");
			return res.tenants;
		},
		enabled: () => toValue(enabled),
		refetchInterval: 60_000,
	});
}

export function useSupportSessions(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "support-sessions"] as const,
		queryFn: async (): Promise<SupportSession[]> => {
			const res = await request<{ sessions: SupportSession[] }>("/v3/platform/support-session");
			return res.sessions;
		},
		enabled: () => toValue(enabled),
	});
}

/**
 * Whether somebody may look inside THIS workspace right now.
 *
 * Read by the shell, for everyone — not a platform route in the guarded sense: it
 * answers about the caller's own workspace, and whoever is being looked at is
 * exactly who has to know.
 */
/** As faixas que ESTE workspace vê — o mesmo relógio modesto do suporte. */
export function useMyNotices(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "notice", "mine"] as const,
		queryFn: async (): Promise<PlatformNotice[]> => {
			const res = await request<{ notices: PlatformNotice[] }>("/v3/platform/notice/mine");
			return res.notices;
		},
		enabled: () => toValue(enabled),
		refetchInterval: 60_000,
	});
}

export function usePlatformNotices(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "notices"] as const,
		queryFn: async (): Promise<PlatformNotice[]> => {
			const res = await request<{ notices: PlatformNotice[] }>("/v3/platform/notices");
			return res.notices;
		},
		enabled: () => toValue(enabled),
	});
}

export function useCreateNotice() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			tenantId: string | null;
			message: string;
			tone: string;
			link: string | null;
			minutes: number;
		}) => request("/v3/platform/notices", { method: "POST", body: input }),
		// A lista E a faixa: sem o "mine" o próprio painel só via o aviso no F5
		// (o relógio de 60s é para os OUTROS navegadores; aqui a causa está na mão).
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["platform", "notices"] });
			void client.invalidateQueries({ queryKey: ["platform", "notice", "mine"] });
		},
	});
}

export function useRevokeNotice() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) => request(`/v3/platform/notices/${id}`, { method: "DELETE" }),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["platform", "notices"] });
			void client.invalidateQueries({ queryKey: ["platform", "notice", "mine"] });
		},
	});
}

export function useMySupportSession(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		queryKey: ["platform", "support-session", "mine"] as const,
		queryFn: async (): Promise<SupportSession | null> => {
			const res = await request<{ session: SupportSession | null }>(
				"/v3/platform/support-session/mine",
			);
			return res.session;
		},
		enabled: () => toValue(enabled),
		// Modest on purpose: the banner has to appear and disappear on its own, and
		// a grant lasts minutes to hours.
		refetchInterval: 60_000,
		// No retry, and this is the one query where that matters: it runs in the
		// SHELL, for every signed-in person. Three retries on a failing server would
		// be three times the traffic from every open tab, to draw nothing.
		retry: false,
	});
}

export function usePlatformLogs(
	enabled: MaybeRefOrGetter<boolean>,
	level: MaybeRefOrGetter<string>,
	scope: MaybeRefOrGetter<string>,
) {
	return useQuery({
		queryKey: ["platform", "logs", () => toValue(level), () => toValue(scope)] as const,
		queryFn: async () => {
			const query = new URLSearchParams({ limit: "200" });
			if (toValue(level)) query.set("level", String(toValue(level)));
			if (toValue(scope)) query.set("scope", String(toValue(scope)));
			return request<{
				durable: boolean;
				note: string;
				scopes: string[];
				entries: LogEntry[];
			}>(`/v3/platform/logs?${query}`);
		},
		enabled: () => toValue(enabled),
		// Logs are the one thing you watch while something is going wrong.
		refetchInterval: 5000,
	});
}

/**
 * Suspending a workspace, archiving it, and letting it back in.
 *
 * `archived` is offered because the enum has always had it (`TENANT_STATUSES`)
 * and `sign-in.ts` already refuses anyone whose workspace is not `active` — so
 * archiving stops the logins with no new code anywhere.
 *
 * A suspended workspace signs nobody in and its outbox stops draining, so the
 * tenant list is stale the moment this succeeds — hence the invalidation lives
 * here, beside the query it invalidates, instead of in the page that calls it.
 * The audit is invalidated too: the act has just written a line.
 */
export function useSetTenantStatus() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; status: TenantStatus }) =>
			request<{ id: string; status: string }>(`/v3/platform/tenants/${input.id}`, {
				method: "PATCH",
				body: { status: input.status },
			}),
		onSuccess: () => invalidatePlatform(client),
	});
}

export function useSetTenantPlatformAdmin() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; enabled: boolean }) =>
			request<{ id: string; platformAdmin: boolean }>(
				`/v3/platform/tenants/${input.id}/platform-admin`,
				{ method: "PATCH", body: { enabled: input.enabled } },
			),
		onSuccess: () => invalidatePlatform(client),
	});
}

/**
 * A exclusão DEFINITIVA — o único ato desta tela sem volta.
 *
 * O servidor tem as duas travas (só `archived`, e o slug digitado viaja no
 * corpo), e a tela repete a segunda: o botão só habilita com o slug batido.
 * As recusas chegam com código — `NOT_ARCHIVED`, `WRONG_SLUG`,
 * `NO_SUCH_TENANT` — e a invalidação é o prefixo inteiro, como nas outras:
 * a lista, o dossiê e a auditoria acabaram de mudar de uma vez.
 */
export function useDeleteTenant() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; confirmSlug: string }) =>
			request<PurgeOutcome>(`/v3/platform/tenants/${input.id}`, {
				method: "DELETE",
				body: { confirmSlug: input.confirmSlug },
			}),
		onSuccess: () => invalidatePlatform(client),
	});
}

export function useGrantSupportSession() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; reason: string; minutes: number }) =>
			request<{ session: SupportSession }>(`/v3/platform/tenants/${input.id}/support-session`, {
				method: "POST",
				body: { reason: input.reason, minutes: input.minutes },
			}),
		onSuccess: () => invalidatePlatform(client),
	});
}

export function useRevokeSupportSession() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (sessionId: string) =>
			request<{ session: SupportSession }>(`/v3/platform/support-session/${sessionId}`, {
				method: "DELETE",
			}),
		onSuccess: () => invalidatePlatform(client),
	});
}

/**
 * One prefix, not a list of them.
 *
 * Every write on this screen changes at least three of its readers — the list,
 * the open grants and the audit line it just wrote — and naming each one is three
 * places to forget the fourth. The whole `platform` prefix is a handful of
 * queries on a screen that loads a few times a day.
 */
function invalidatePlatform(client: ReturnType<typeof useQueryClient>): void {
	void client.invalidateQueries({ queryKey: ["platform"] });
}

/**
 * O saldo de um workspace, como o console o lê — os nomes são os do servidor.
 *
 * `plan` zera no fechamento do ciclo e `bonus` sobrevive: são dois bolsos e não
 * um total, porque dar no errado é dar créditos que somem na virada do mês.
 */
export interface TenantCredits {
	readonly tenantId: string;
	readonly plan: number;
	readonly bonus: number;
	readonly total: number;
	readonly blockedByAdmin: boolean;
	readonly blockedReason: string | null;
	readonly cycleStart: string | null;
	readonly cycleEnd: string | null;
	readonly updatedAt: string;
}

export function useTenantCredits(id: MaybeRefOrGetter<string>) {
	return useQuery({
		queryKey: computed(() => ["platform", "credits", toValue(id)] as const),
		queryFn: () => request<TenantCredits>(`/v3/platform/tenants/${toValue(id)}/credits`),
		enabled: computed(() => toValue(id).length > 0),
	});
}

/**
 * CRÉDITO NA MÃO — a rota existia e nenhuma tela a chamava.
 *
 * `POST /v3/platform/tenants/:id/credits` está escrita, guardada por
 * `PlatformAdminGuard`, exige um motivo de dez caracteres e deixa uma linha no
 * ledger. Só nunca teve chamador: dar crédito a um workspace de teste era um
 * `curl` que só quem conhece o código sabia montar.
 *
 * Positivo dá, negativo tira — a mesma rota para os dois, porque são o mesmo
 * ato: um administrador a mudar um número à mão, com um motivo. Duas rotas
 * seriam dois lugares para o motivo ser esquecido.
 */
/**
 * O RAZÃO deste workspace, do lado de quem mexe nele.
 *
 * Só os movimentos manuais: dar crédito e não conseguir ver, um mês depois, que
 * se deu — nem por quê — é o motivo obrigatório a ser escrito para ninguém.
 */
export interface TenantLedgerEntry {
	readonly id: string;
	readonly at: string;
	readonly kind: string;
	readonly pool: string;
	readonly deltaCredits: number;
	readonly planAfter: number;
	readonly bonusAfter: number;
	readonly reason: string | null;
}

export function useTenantCreditLedger(id: MaybeRefOrGetter<string>) {
	return useQuery({
		queryKey: computed(() => ["platform", "credit-ledger", toValue(id)] as const),
		queryFn: () =>
			request<{ entries: TenantLedgerEntry[] }>(
				`/v3/platform/tenants/${toValue(id)}/credits/ledger`,
			),
		enabled: computed(() => toValue(id).length > 0),
	});
}

/**
 * A PARADA MANUAL, e o levantá-la. Um saldo cheio não a desfaz — dois fatos,
 * dois remédios —, e o motivo é exigido nas duas direções: por que alguém foi
 * solto é tão perguntável quanto por que foi parado.
 */
export function useBlockCredits() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { tenantId: string; blocked: boolean; reason: string }) => {
			const { tenantId, ...body } = input;
			return request<TenantCredits>(`/v3/platform/tenants/${tenantId}/credits/block`, {
				method: "PATCH",
				body,
			});
		},
		onSuccess: () => client.invalidateQueries({ queryKey: ["platform"] }),
	});
}

export function useAdjustCredits() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: {
			tenantId: string;
			deltaCredits: number;
			pool: "plan" | "bonus";
			reason: string;
		}) => {
			const { tenantId, ...body } = input;
			return request<TenantCredits>(`/v3/platform/tenants/${tenantId}/credits`, {
				method: "POST",
				body,
			});
		},
		onSuccess: () => client.invalidateQueries({ queryKey: ["platform"] }),
	});
}

export interface TenantSite {
	readonly id: string;
	readonly name: string;
	readonly isDefault: boolean;
	readonly isPublished: boolean;
	readonly hasDraft: boolean;
	readonly previewKey: string;
	readonly publishedAt: string | null;
	readonly updatedAt: string | null;
	/** Endereço público — nulo enquanto o site não tem subdomínio nem domínio verificado. */
	readonly url: string | null;
}

/** Os sites de um workspace, lidos só quando a linha da lista é aberta. */
export function useTenantSites(id: MaybeRefOrGetter<string | null>) {
	return useQuery({
		queryKey: ["platform", "tenant-sites", () => toValue(id)] as const,
		queryFn: () =>
			request<{ sites: TenantSite[] }>(`/v3/platform/tenants/${toValue(id)}/sites`).then(
				(r) => r.sites,
			),
		enabled: () => !!toValue(id),
	});
}

/**
 * O OLHINHO: abre o workspace numa aba nova COMO uma pessoa do cliente (o dono,
 * sem escolha), com o papel dela e sem a faixa de suporte — para ver o que ela vê. A aba de origem continua logada — o modo suporte vive no
 * sessionStorage da aba nova (`enterSupport`).
 *
 * A aba é aberta ANTES do pedido, no próprio clique: aberta depois do `await`,
 * o navegador a trata como pop-up e bloqueia.
 */
export async function peekTenant(id: string, userId?: string): Promise<void> {
	const tab = window.open("about:blank", "_blank");
	try {
		const { session } = await request<{ session: SupportSession }>(
			`/v3/platform/tenants/${id}/peek`,
			// Sem `userId`, o servidor entra como o dono.
			{ method: "POST", body: userId ? { userId } : {} },
		);
		if (!session.accessToken) throw new Error("no access token");
		const url = `${window.location.origin}/suporte/${session.accessToken}`;
		if (tab) tab.location.href = url;
		else window.open(url, "_blank");
	} catch (e) {
		tab?.close();
		throw e;
	}
}
